import { useState, useRef, useCallback, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import {
  Globe, MapPin, Camera,
  CheckCircle2, Upload, Building2, Plus, Briefcase, X,
} from 'lucide-react'
import {
  Badge,
  Button,
  ErrorState,
  IconButton,
  Modal,
  type BadgeProps,
} from '@/components/ui'
import { cn } from '@/lib/utils.ts'

type DetectedType = 'google-places' | 'website' | 'image' | 'job-posting' | null

interface QuickCaptureResult {
  prospect: Record<string, unknown>
  message: string
  ocrText?: string
}

interface CapturedEntry {
  companyName: string
  type: DetectedType
  success: boolean
}

function detectInputType(value: string): DetectedType {
  const trimmed = value.trim()
  if (!trimmed) return null
  if (/google\.[a-z.]+\/maps/i.test(trimmed) || /maps\.google/i.test(trimmed) || /goo\.gl\/maps/i.test(trimmed) || /maps\.app\.goo\.gl/i.test(trimmed)) {
    return 'google-places'
  }
  if (/indeed\.|stepstone\.|linkedin\.com\/jobs|xing\.com\/jobs|monster\.|glassdoor\.|stellenanzeigen\.de\/|jobs\.de\/|kimeta\.|arbeitsagentur\./i.test(trimmed)) {
    return 'job-posting'
  }
  if (/^https?:\/\//i.test(trimmed) || /^www\./i.test(trimmed) || /\.[a-z]{2,}$/i.test(trimmed)) {
    return 'website'
  }
  return null
}

interface TypeInfo {
  label: string
  icon: React.ReactNode
  variant: BadgeProps['variant']
}

function getTypeInfo(type: DetectedType): TypeInfo | null {
  switch (type) {
    case 'google-places':
      return { label: 'Google Maps', icon: <MapPin className="h-3.5 w-3.5" />, variant: 'danger' }
    case 'website':
      return { label: 'Website', icon: <Globe className="h-3.5 w-3.5" />, variant: 'info' }
    case 'job-posting':
      return { label: 'Stellenanzeige', icon: <Briefcase className="h-3.5 w-3.5" />, variant: 'warning' }
    case 'image':
      return { label: 'Bild/Foto', icon: <Camera className="h-3.5 w-3.5" />, variant: 'accent' }
    default:
      return null
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getErrorMessage(error: any): string {
  const data = error?.response?.data
  if (data?.error) {
    return data.details ? `${data.error}\n${typeof data.details === 'string' ? data.details.slice(0, 300) : JSON.stringify(data.details).slice(0, 300)}` : data.error
  }
  if (error instanceof Error) return error.message
  return 'Ein Fehler ist aufgetreten.'
}

export function QuickCaptureModal({ onClose, onSave, onManualEntry }: { onClose: () => void; onSave: () => void; onManualEntry?: () => void }) {
  const [input, setInput] = useState('')
  const [detectedType, setDetectedType] = useState<DetectedType>(null)
  const [typeOverride, setTypeOverride] = useState<'website' | 'job-posting' | null>(null)
  const [imageData, setImageData] = useState<string | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [captured, setCaptured] = useState<CapturedEntry[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()

  useEffect(() => {
    if (imageData) {
      setDetectedType('image')
    } else {
      setDetectedType(detectInputType(input))
    }
  }, [input, imageData])

  const effectiveType: DetectedType =
    (detectedType === 'website' || detectedType === 'job-posting') && typeOverride
      ? typeOverride
      : detectedType

  function toggleJobPostingType() {
    if (effectiveType === 'website') setTypeOverride('job-posting')
    else if (effectiveType === 'job-posting') setTypeOverride('website')
  }

  const capture = useMutation({
    mutationFn: async (): Promise<QuickCaptureResult> => {
      const type = effectiveType
      if (type === 'image') {
        if (!imageData) throw new Error('Bitte ein Bild auswählen.')
        const { data } = await apiClient.post('/acquisition/prospects/quick-capture/image', { imageBase64: imageData })
        return data
      }
      const trimmed = input.trim()
      if (!trimmed) throw new Error('Bitte eine URL oder einen Link eingeben.')
      const endpoint = type === 'google-places'
        ? '/acquisition/prospects/quick-capture/google-places'
        : type === 'job-posting'
          ? '/acquisition/prospects/quick-capture/job-posting'
          : '/acquisition/prospects/quick-capture/website'
      const { data } = await apiClient.post(endpoint, { url: trimmed })
      return data
    },
    onSuccess: (data) => {
      const companyName = (data.prospect?.companyName as string) || 'Firma'
      setCaptured((prev) => [...prev, { companyName, type: effectiveType, success: true }])
      setInput('')
      setImageData(null)
      setImagePreview(null)
      setDetectedType(null)
      setTypeOverride(null)
      queryClient.invalidateQueries({ queryKey: ['acquisition'] })
      setTimeout(() => inputRef.current?.focus(), 100)
    },
    onError: () => {
      // Keep input so user can retry or correct
    },
  })

  const handleImageFile = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) return
    const reader = new FileReader()
    reader.onload = (e) => {
      const result = e.target?.result as string
      setImageData(result)
      setImagePreview(result)
      setInput('')
    }
    reader.readAsDataURL(file)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) handleImageFile(file)
  }, [handleImageFile])

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData.items
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile()
        if (file) handleImageFile(file)
        e.preventDefault()
        return
      }
    }
  }, [handleImageFile])

  function clearImage() {
    setImageData(null)
    setImagePreview(null)
    setDetectedType(null)
    setTimeout(() => inputRef.current?.focus(), 50)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!effectiveType) return
    capture.mutate()
  }

  function handleClose() {
    if (captured.length > 0) {
      onSave()
    } else {
      onClose()
    }
  }

  const hasInput = effectiveType !== null
  const typeInfo = effectiveType ? getTypeInfo(effectiveType) : null
  const canToggleType = effectiveType === 'website' || effectiveType === 'job-posting'

  return (
    <Modal
      open
      onOpenChange={(open) => { if (!open) handleClose() }}
      size="md"
      title={
        <span className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-brand-500" />
          Schnellerfassung
          {captured.length > 0 && (
            <Badge variant="neutral" size="sm">{captured.length} erfasst</Badge>
          )}
        </span>
      }
    >
      <form
        onSubmit={handleSubmit}
        className="space-y-4"
        onPaste={handlePaste}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        {captured.length > 0 && (
          <div className="space-y-1.5 max-h-32 overflow-y-auto">
            {captured.map((entry, i) => {
              const info = getTypeInfo(entry.type)
              return (
                <div key={i} className="flex items-center gap-2 text-caption">
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />
                  <span className="text-gray-700 font-medium truncate">{entry.companyName}</span>
                  {info && (
                    <Badge variant={info.variant} size="sm">
                      {info.icon}
                      {info.label}
                    </Badge>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {!imageData ? (
          <div>
            <div
              className={cn(
                'relative rounded-md border-2 transition-all',
                dragOver ? 'border-brand-500 bg-brand-50' : 'border-gray-200',
              )}
            >
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => { setInput(e.target.value); capture.reset() }}
                placeholder="URL einfügen, Bild ablegen, oder Strg+V zum Einfügen..."
                className="w-full px-4 py-3.5 bg-transparent rounded-md text-body text-gray-900 placeholder:text-gray-400 focus:outline-none"
                autoFocus
              />
              {typeInfo && (
                canToggleType ? (
                  <button
                    type="button"
                    onClick={toggleJobPostingType}
                    title={effectiveType === 'website' ? 'Als Stellenanzeige erfassen' : 'Als Website erfassen'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 hover:opacity-60 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 rounded-full"
                  >
                    <Badge variant={typeInfo.variant} size="sm">
                      {typeInfo.icon}
                      {typeInfo.label}
                    </Badge>
                  </button>
                ) : (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Badge variant={typeInfo.variant} size="sm">
                      {typeInfo.icon}
                      {typeInfo.label}
                    </Badge>
                  </div>
                )
              )}
            </div>
            <p className="text-caption text-gray-400 mt-2">
              Google Maps, Website, Stellenanzeige (Indeed, Stepstone...) oder Bild — wird automatisch erkannt
            </p>
          </div>
        ) : (
          <div className="relative">
            <div className="border-2 border-accent-200 bg-accent-50 rounded-md p-4 text-center">
              <img src={imagePreview!} alt="Vorschau" className="max-h-48 mx-auto rounded-md shadow-sm" />
              <div className="flex items-center justify-center gap-1 mt-2">
                <Camera className="h-3.5 w-3.5 text-accent-600" />
                <span className="text-caption text-accent-700 font-medium">Bild erkannt</span>
              </div>
            </div>
            <IconButton
              type="button"
              onClick={clearImage}
              aria-label="Bild entfernen"
              variant="secondary"
              size="xs"
              className="absolute top-2 right-2 rounded-full"
              icon={<X className="h-4 w-4" />}
            />
          </div>
        )}

        {!imageData && !input && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="w-full flex items-center gap-2 p-3 rounded-md border border-dashed border-gray-300 cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
          >
            <Upload className="h-4 w-4 text-gray-400" />
            <span className="text-caption text-gray-500">Oder Bild auswählen (Firmenauto, Schild, Visitenkarte...)</span>
          </button>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) handleImageFile(file)
          }}
        />

        {capture.isError && (
          <ErrorState
            size="sm"
            description={getErrorMessage(capture.error)}
            details={capture.error ? JSON.stringify(capture.error, Object.getOwnPropertyNames(capture.error as object), 2) : undefined}
          />
        )}

        <div className="flex items-center justify-between pt-2">
          <div>
            {onManualEntry && (
              <Button type="button" variant="link" size="sm" onClick={onManualEntry}>
                Manuell eingeben
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button type="button" variant="secondary" size="sm" onClick={handleClose}>
              {captured.length > 0 ? 'Fertig' : 'Abbrechen'}
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={!hasInput}
              loading={capture.isPending}
              leadingIcon={<Plus className="h-4 w-4" />}
            >
              {capture.isPending ? 'Wird erkannt...' : 'Erfassen'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  )
}
